import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { apiDelete, apiGet, apiPost, apiPut } from "../../lib/api";

type Preset = {
  id: string;
  country_code: string;
  default_locale: string;
  supported_locales: string[];
  default_channel_strategy: "telegram_first" | "whatsapp_first" | "email_first";
  allowed_channel_strategies: string[];
  default_currency: string;
  default_plan_code: string;
  requires_captcha: boolean;
};

const emptyPreset: Omit<Preset, "id"> = {
  country_code: "US",
  default_locale: "en",
  supported_locales: ["en"],
  default_channel_strategy: "email_first",
  allowed_channel_strategies: ["telegram_first", "whatsapp_first", "email_first"],
  default_currency: "USD",
  default_plan_code: "trial_14d",
  requires_captcha: false,
};

export function SystemCountryPresetsPage() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(emptyPreset);
  const [editing, setEditing] = useState<string>("");
  const list = useQuery({ queryKey: ["sys-country-presets"], queryFn: () => apiGet<{ items: Preset[] }>("/v3/sys/presets/countries") });

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        ...draft,
        country_code: draft.country_code.toUpperCase(),
        default_currency: draft.default_currency.toUpperCase(),
        supported_locales: draft.supported_locales,
        allowed_channel_strategies: draft.allowed_channel_strategies,
      };
      return editing ? apiPut(`/v3/sys/presets/countries/${payload.country_code}`, payload) : apiPost("/v3/sys/presets/countries", payload);
    },
    onSuccess: async () => {
      setDraft(emptyPreset);
      setEditing("");
      await qc.invalidateQueries({ queryKey: ["sys-country-presets"] });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (countryCode: string) => apiDelete(`/v3/sys/presets/countries/${countryCode}`),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["sys-country-presets"] }); },
  });

  return (
    <div className="grid gap-4">
      <h1 className="text-2xl font-semibold">Country Presets</h1>
      <section className="panel p-4 grid gap-2">
        <h2 className="text-lg font-semibold">{editing ? "Edit Preset" : "Create Preset"}</h2>
        <div className="grid md:grid-cols-4 gap-2">
          <input className="input" placeholder="Country" value={draft.country_code} onChange={(e) => setDraft({ ...draft, country_code: e.target.value.toUpperCase() })} />
          <input className="input" placeholder="Default locale" value={draft.default_locale} onChange={(e) => setDraft({ ...draft, default_locale: e.target.value.toLowerCase() })} />
          <input className="input" placeholder="Currency" value={draft.default_currency} onChange={(e) => setDraft({ ...draft, default_currency: e.target.value.toUpperCase() })} />
          <input className="input" placeholder="Plan code" value={draft.default_plan_code} onChange={(e) => setDraft({ ...draft, default_plan_code: e.target.value })} />
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          <input className="input" placeholder="Supported locales (comma)" value={draft.supported_locales.join(",")} onChange={(e) => setDraft({ ...draft, supported_locales: e.target.value.split(",").map((v) => v.trim()).filter(Boolean) })} />
          <input className="input" placeholder="Allowed strategies (comma)" value={draft.allowed_channel_strategies.join(",")} onChange={(e) => setDraft({ ...draft, allowed_channel_strategies: e.target.value.split(",").map((v) => v.trim()).filter(Boolean) })} />
        </div>
        <div className="grid md:grid-cols-3 gap-2 items-center">
          <select className="input" value={draft.default_channel_strategy} onChange={(e) => setDraft({ ...draft, default_channel_strategy: e.target.value as any })}>
            <option value="telegram_first">telegram_first</option>
            <option value="whatsapp_first">whatsapp_first</option>
            <option value="email_first">email_first</option>
          </select>
          <label className="flex gap-2 items-center text-sm"><input type="checkbox" checked={draft.requires_captcha} onChange={(e) => setDraft({ ...draft, requires_captcha: e.target.checked })} /> requires captcha</label>
          <button className="btn" disabled={!draft.country_code.trim()} onClick={() => saveMutation.mutate()}>{editing ? "Update" : "Create"}</button>
        </div>
      </section>

      <section className="panel p-4">
        <h2 className="text-lg font-semibold">Presets</h2>
        <div className="grid gap-2 mt-2 text-sm">
          {(list.data?.items ?? []).map((row) => (
            <div key={row.id} className="rounded border border-slate-200 bg-white p-3 grid gap-2">
              <div className="flex flex-wrap justify-between gap-2">
                <div>
                  <p><strong>{row.country_code}</strong> � {row.default_currency} � {row.default_channel_strategy}</p>
                  <p className="text-slate-500">Locales: {row.supported_locales.join(", ")}</p>
                </div>
                <div className="flex gap-2">
                  <button className="btn secondary" onClick={() => { setEditing(row.country_code); setDraft({ ...row }); }}>Edit</button>
                  <button className="btn" onClick={() => deleteMutation.mutate(row.country_code)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
          {!(list.data?.items ?? []).length ? <p className="text-slate-500">No presets configured.</p> : null}
        </div>
      </section>
    </div>
  );
}
